// NOTE: NSAI-7852 — models now and then write a ```ui block with one bracket
// too many (Claude Fable 5.1 closed a comparison block with `}\n }]}`), one
// too few, or a trailing comma, and a single wrong character used to cost the
// user the whole interface. This fixes exactly those characters.
//
// Two modes. By default it only REMOVES text: a stray closing bracket and a
// trailing comma. With `canClose` it may also ADD up to
// MAX_UI_JSON_ADDED_BRACKETS missing closing brackets — only for a block that
// has finished streaming, because a streaming block is missing its closers for
// most of its life and closing them for it would draw a half-written
// interface. Whatever this returns still goes through JSON.parse and the full
// schema, so a wrong guess is refused rather than drawn.
//
// It runs on every streamed chunk that is not JSON yet, so the walk stays
// linear: open brackets are counted per type, so a stray closer is recognised
// without searching the stack, and the stack is searched only when that search
// leads to adding brackets — which stops at the limit.

import {
  MAX_UI_JSON_ADDED_BRACKETS,
  UI_JSON_CLOSER_BY_OPENER,
  UI_JSON_OPENER_BY_CLOSER,
  UI_JSON_WHITESPACE_PATTERN,
} from "../../constants/ui";
import type { IRepairUiJsonProps } from "../../types/ui";

interface IOpenBrackets {
  stack: string[];
  counts: Map<string, number>;
}

interface IPushBracketProps {
  openBrackets: IOpenBrackets;
  opener: string;
}

interface ICloseBracketsProps {
  output: string[];
  openBrackets: IOpenBrackets;
  depth: number;
}

// NOTE: Removes a comma left right before a closing bracket (`[1, 2,]`).
// Returns whether it removed one.
const dropTrailingComma = (output: string[]): boolean => {
  let index = output.length - 1;

  while (index >= 0 && UI_JSON_WHITESPACE_PATTERN.test(output[index] ?? "")) {
    index -= 1;
  }

  if (output[index] !== ",") {
    return false;
  }

  output.splice(index, 1);
  return true;
};

const pushBracket = ({
  openBrackets: { stack, counts },
  opener,
}: IPushBracketProps): void => {
  stack.push(opener);
  counts.set(opener, (counts.get(opener) ?? 0) + 1);
};

const popBracket = ({ stack, counts }: IOpenBrackets): string => {
  const opener = stack.pop() ?? "";
  counts.set(opener, (counts.get(opener) ?? 1) - 1);
  return opener;
};

// NOTE: Closes every bracket still open above `depth` on the stack, innermost
// first.
const closeBrackets = ({
  output,
  openBrackets,
  depth,
}: ICloseBracketsProps): void => {
  while (openBrackets.stack.length > depth) {
    dropTrailingComma(output);
    output.push(UI_JSON_CLOSER_BY_OPENER.get(popBracket(openBrackets)) ?? "");
  }
};

// NOTE: Walks the source once, skipping string contents, and keeps a stack of
// the brackets still open. A closing bracket that does not match the one on
// top of the stack is either stray — dropped — or, with `canClose`, closes an
// outer bracket whose inner ones were never closed: those are closed first.
// With `canClose` the brackets still open at the end are closed too. Returns
// null when nothing needed fixing, or when more than the allowed number of
// brackets would have to be added.
export const repairUiJson = ({
  rawJson,
  canClose = false,
}: IRepairUiJsonProps): string | null => {
  const output: string[] = [];
  const openBrackets: IOpenBrackets = { stack: [], counts: new Map() };
  let isInString = false;
  let isEscaped = false;
  let isChanged = false;
  let addedCount = 0;

  for (const character of rawJson) {
    if (isInString) {
      output.push(character);

      if (isEscaped) {
        isEscaped = false;
      } else if (character === "\\") {
        isEscaped = true;
      } else if (character === '"') {
        isInString = false;
      }

      continue;
    }

    const expectedOpener = UI_JSON_OPENER_BY_CLOSER.get(character);

    if (expectedOpener === undefined) {
      if (character === '"') {
        isInString = true;
      } else if (UI_JSON_CLOSER_BY_OPENER.has(character)) {
        pushBracket({ openBrackets, opener: character });
      }

      output.push(character);
      continue;
    }

    if (openBrackets.stack.at(-1) !== expectedOpener) {
      isChanged = true;

      if (!canClose || (openBrackets.counts.get(expectedOpener) ?? 0) === 0) {
        continue;
      }

      const depth = openBrackets.stack.lastIndexOf(expectedOpener) + 1;
      addedCount += openBrackets.stack.length - depth;

      if (addedCount > MAX_UI_JSON_ADDED_BRACKETS) {
        return null;
      }

      closeBrackets({ output, openBrackets, depth });
    }

    isChanged = dropTrailingComma(output) || isChanged;
    popBracket(openBrackets);
    output.push(character);
  }

  if (canClose && !isInString && openBrackets.stack.length > 0) {
    isChanged = true;
    addedCount += openBrackets.stack.length;

    if (addedCount > MAX_UI_JSON_ADDED_BRACKETS) {
      return null;
    }

    closeBrackets({ output, openBrackets, depth: 0 });
  }

  return isChanged ? output.join("") : null;
};
